import types from 'actions/types'
import {combineReducers} from 'redux'
import {filter, isNil, orderBy, sumBy} from 'lodash'
import contactImport from './contactImport'
import teams from './teams'
import printTemplateEditor from './printTemplateEditor'
import trustedDevice from './trustedDevice'
import tFASettings from "reducers/settingsContext/tFASettings"
import pricingCalculators from "reducers/settingsContext/pricingCalculators"

const objExcludeProps = (obj, excludedProps) => {
  const ret = {}
  for (const i in obj) {
    if (excludedProps.indexOf(i) === -1 && obj.hasOwnProperty(i)) ret[i] = obj[i]
  }
  return ret
}

const loginsUpdate = (state, action, target, newStateFtn) => {
  const newState = (typeof newStateFtn === 'function') ? newStateFtn : (act) => {
    return act.data[target]
  }
  switch (action.type) {
    case types.SETTINGS_ADD_USER_SUCCESS:
    case types.SETTINGS_REMOVE_USER_SUCCESS:
    case types.SETTINGS_USER_ROLE_UPDATE_SUCCESS:
    case types.SETTINGS_ROLE_ADD_SUCCESS:
    case types.SETTINGS_ROLE_DELETE_SUCCESS:
    case types.SETTINGS_ROLE_PERMISSION_ADD_SUCCESS:
    case types.SETTINGS_ROLE_PERMISSION_DELETE_SUCCESS:
    case types.SETTINGS_LICENSE_LOAD_SUCCESS:
    case types.SETTINGS_LICENCE_SET:
      return newState(action)
    default:
      return state
  }
}

const markContactAsDirty = (contacts, userId) => {
  const contact = Object.values(contacts).find(contact => contact.userId === userId)
  if (isNil(contact))
    return contacts

  return {
    ...contacts,
    [contact.id]: {
      ...contact,
      dirty: true,
    },
  }
}

const markContactAsClean = (contacts, userId) => {
  const contact = Object.values(contacts).find(contact => contact.userId === userId)
  if (isNil(contact))
    return contacts

  return {
    ...contacts,
    [contact.id]: {
      ...contact,
      dirty: false,
      error: null,
    },
  }
}

const markContactAsFailed = (contacts, userId, error) => {
  const contact = Object.values(contacts).find(contact => contact.userId === userId)
  if (isNil(contact))
    return contacts

  return {
    ...contacts,
    [contact.id]: {
      ...contact,
      error,
    },
  }
}

const activityResourceTypeOpen = (state = {}, action) => {
  const newState = {...state}
  switch (action.type) {
    case types.SETTINGS_ACTIVITY_RESOURCE_TYPE_OPEN:
      newState[action.id] = true
      return newState
    case types.SETTINGS_ACTIVITY_RESOURCE_TYPE_CLOSE:
      newState[action.id] = false
      return newState
    default:
      return state
  }
}

const contacts = (state = {}, action) => {
  switch (action.type) {
    case types.SETTINGS_UPDATE_USER_ATTEMPT:
      return markContactAsDirty(state, action.userId)
    case types.SETTINGS_UPDATE_USER_FAILURE:
      return markContactAsFailed(state, action.userId, action.message)
    case types.SETTINGS_UPDATE_USER_SUCCESS:
      return markContactAsClean(state, action.userId)
    default:
      return loginsUpdate(state, action, 'contacts')
  }
}

const externalContacts = (state = {}, action) => {
  switch (action.type) {
    case types.SETTINGS_UPDATE_USER_ATTEMPT:
      return markContactAsDirty(state, action.userId)
    case types.SETTINGS_UPDATE_USER_SUCCESS:
      return markContactAsClean(state, action.userId)
    default:
      return loginsUpdate(state, action, 'externalContacts')
  }
}

const licences = (state = {}, action) => {
  return loginsUpdate(state, action, 'licences')
}

const packages = (state = {}, action) => {
  return loginsUpdate(state, action, 'packages')
}

const loginsRemaining = (state = 0, action) => {
  return loginsUpdate(state, action, null, (act) => {
    return sumBy(filter(act.data.licences, l => l.isMaster), l => l.remaining)
  })
}

const roles = (state = {}, action) => {
  return loginsUpdate(state, action, 'roles')
}

const permissions = (state = [], action) => {
  return loginsUpdate(state, action, 'permissions')
}

const reorderHelper = (state, ids) => {
  return [
    ...state.filter(t => ids.indexOf(t.id) === -1),
    ...state.filter(t => ids.indexOf(t.id) !== -1).map(t => ({...t, order: ids.indexOf(t.id)})),
  ]
}

const tags = (state = [], action) => {
  let data
  switch (action.type) {
    case types.USERFIELDS_READ_SUCCESS:
      return action.data
    case types.USERFIELDS_ADD_SUCCESS:
      data = (action.type === types.USERFIELDS_ADD_ATTEMPT) ? objExcludeProps(action, ['type']) : action.data
      return [...state, data]
    case types.USERFIELD_MOVE_SUCCESS:
    case types.USERFIELDS_UPDATE_SUCCESS:
      data = (action.type === types.USERFIELDS_UPDATE_ATTEMPT) ? objExcludeProps(action, ['type']) : action.data
      return [...state.filter(t => t.id !== action.data.id), data]
    case types.USERFIELDS_DELETE_ATTEMPT:
      return [...state.filter(t => t.id !== action.id)]
    case types.USERFIELDS_DELETE_SUCCESS:
      return [...state.filter(t => t.id !== action.data.id)]
      // Assume move is successful
    case types.USERFIELDS_MOVE_ATTEMPT:
      return [
        ...state.filter(t => t.id !== action.tagId),
        ...state.filter(t => t.id === action.tagId).map(t => ({...t, categoryId: action.categoryId})),
      ]
      // Assume reorder is successful
    case types.USERFIELDS_REORDER_ATTEMPT:
      return reorderHelper(state, action.ids)
    case types.USERFIELDS_REORDER_SUCCESS:
      return reorderHelper(state, action.data.map(c => c.id))
    default:
      return state
  }
}

const tagCategories = (state = [], action) => {
  let data
  switch (action.type) {
    case types.USERFIELDS_CATEGORY_READ_SUCCESS:
      return action.data
    case types.USERFIELDS_CATEGORY_ADD_ATTEMPT:
    case types.USERFIELDS_CATEGORY_ADD_SUCCESS:
      data = (action.type === types.USERFIELDS_CATEGORY_ADD_ATTEMPT) ? objExcludeProps(action, ['type']) : action.data
      return [...state, data]
    case types.USERFIELDS_CATEGORY_UPDATE_ATTEMPT:
    case types.USERFIELDS_CATEGORY_UPDATE_SUCCESS:
      data = (action.type === types.USERFIELDS_CATEGORY_UPDATE_ATTEMPT) ? objExcludeProps(action, ['type']) : action.data
      return [...state.filter(t => t.id !== data.id), ...state.filter(t => t.id === data.id).map(t => ({
        ...t,
        name: data.name,
      }))]
    case types.USERFIELDS_CATEGORY_DELETE_SUCCESS:
      return [...state.filter(t => t.id !== action.data.id)]
    case types.USERFIELDS_CATEGORY_REORDER_ATTEMPT:
      return reorderHelper(state, action.ids)
    case types.USERFIELDS_CATEGORY_REORDER_SUCCESS:
      return reorderHelper(state, action.data.map(c => c.id))
    default:
      return state
  }
}

const isUpdatingLogins = (state = false, action) => {
  switch (action.type) {
    case types.SETTINGS_ADD_USER_ATTEMPT:
    case types.SETTINGS_REMOVE_USER_ATTEMPT:
    case types.SETTINGS_UPDATE_USER_ATTEMPT:
      return true
    case types.SETTINGS_ADD_USER_SUCCESS:
    case types.SETTINGS_ADD_USER_FAILURE:
    case types.SETTINGS_REMOVE_USER_SUCCESS:
    case types.SETTINGS_REMOVE_USER_FAILURE:
    case types.SETTINGS_UPDATE_USER_SUCCESS:
    case types.SETTINGS_UPDATE_USER_FAILURE:
      return false
    default:
      return state
  }
}

export const documentFolderTemplates = (state = [], action) => {
  switch (action.type) {
    case types.DOCUMENTS_LOAD_FOLDER_TEMPLATES_ATTEMPT:
      return []

    case types.DOCUMENTS_LOAD_FOLDER_TEMPLATES_SUCCESS:
      return action.data

    case types.DOCUMENTS_ADD_FOLDER_TEMPLATE_SUCCESS: {
      const updatedState = [...state, action.data]
      return orderBy(updatedState, 'name')
    }

    case types.DOCUMENTS_UPDATE_FOLDER_TEMPLATE_ATTEMPT: {
      const updatedState = state.map(template =>
          template.id === action.documentFolderTemplate.id
              ? action.documentFolderTemplate
              : template)

      return orderBy(updatedState, 'name')
    }

    case types.DOCUMENTS_DELETE_FOLDER_TEMPLATE_ATTEMPT:
      return state.filter(template => template.id !== action.id)

    default:
      return state
  }
}

export const automaticReminderConfigs = (state = [], action) => {
  switch (action.type) {
    case types.AUTOMATIC_REMINDER_CONFIG_READ_SUCCESS:
    case types.AUTOMATIC_REMINDER_CONFIG_ADD_SUCCESS:
    case types.AUTOMATIC_REMINDER_CONFIG_DELETE_SUCCESS:
      return action.data

    case types.AUTOMATIC_REMINDER_CONFIG_UPDATE_SUCCESS: {
      const {id, days, result} = action.data
      if (result && result.length > 1) {
        return result.map(el => ({...el, value: el.days}))
      }
      return state.map(item => item.id === id ? {...item, value: days} : item)
    }

    default:
      return state
  }
}

export const loadingAutomaticReminderConfigs = (state = false, action) => {
  switch (action.type) {
    case types.AUTOMATIC_REMINDER_CONFIG_READ_ATTEMPT:
    case types.AUTOMATIC_REMINDER_CONFIG_ADD_ATTEMPT:
    case types.AUTOMATIC_REMINDER_CONFIG_UPDATE_ATTEMPT:
    case types.AUTOMATIC_REMINDER_CONFIG_DELETE_ATTEMPT:
      return true

    case types.AUTOMATIC_REMINDER_CONFIG_READ_SUCCESS:
    case types.AUTOMATIC_REMINDER_CONFIG_READ_FAILURE:
    case types.AUTOMATIC_REMINDER_CONFIG_ADD_SUCCESS:
    case types.AUTOMATIC_REMINDER_CONFIG_ADD_FAILURE:
    case types.AUTOMATIC_REMINDER_CONFIG_UPDATE_SUCCESS:
    case types.AUTOMATIC_REMINDER_CONFIG_UPDATE_FAILURE:
    case types.AUTOMATIC_REMINDER_CONFIG_DELETE_SUCCESS:
    case types.AUTOMATIC_REMINDER_CONFIG_DELETE_FAILURE:
      return false

    default:
      return state
  }
}

export const supportTokens = (state = [], action) => {
  switch (action.type) {
    case types.SETTINGS_SUPPORT_TOKENS_READ_SUCCESS:
      return action.data
    case types.SETTINGS_SUPPORT_TOKENS_ADD_SUCCESS:
      return [...state, action.supportToken]
    case types.SETTINGS_SUPPORT_TOKENS_DELETE_SUCCESS:
      return state.filter(supportToken => supportToken.token !== action.token)
    default:
      return state
  }
}

export const loadingSupportTokens = (state = false, action) => {
  switch (action.type) {
    case types.SETTINGS_SUPPORT_TOKENS_READ_ATTEMPT:
    case types.SETTINGS_SUPPORT_TOKENS_ADD_ATTEMPT:
    case types.SETTINGS_SUPPORT_TOKENS_DELETE_ATTEMPT:
      return true
    case types.SETTINGS_SUPPORT_TOKENS_READ_SUCCESS:
    case types.SETTINGS_SUPPORT_TOKENS_READ_FAILURE:
    case types.SETTINGS_SUPPORT_TOKENS_ADD_SUCCESS:
    case types.SETTINGS_SUPPORT_TOKENS_ADD_FAILURE:
    case types.SETTINGS_SUPPORT_TOKENS_DELETE_SUCCESS:
    case types.SETTINGS_SUPPORT_TOKENS_DELETE_FAILURE:
      return false
    default:
      return state
  }
}

export default combineReducers({
  activityResourceTypeOpen,
  automaticReminderConfigs,
  contacts,
  documentFolderTemplates,
  externalContacts,
  contactImport,
  trustedDevice,
  licences,
  packages,
  loginsRemaining,
  permissions,
  printTemplateEditor,
  roles,
  loadingAutomaticReminderConfigs,
  supportTokens,
  loadingSupportTokens,
  tags,
  tagCategories,
  teams,
  isUpdatingLogins,
  tFASettings,
  pricingCalculators,
})
